import { ResponsiveSection } from '@stitch-fix/mode-react';
import { useState } from 'react';
import { G } from '@mobily/ts-belt';
import { recordPageReady, recordMark } from '@stitch-fix/client-analytics-reporter';
import { useBoughtItemsMachine } from '../../utils/machines/boughtItems/context-provider';
import WardrobeNavigation from '../WardrobeNavigation/WardrobeNavigation';
import { BoughtItemsSuccess } from './BoughtItemsSuccess';
import { BoughtItemsFragment, useGetBoughtItemsQuery, GetBoughtItemsQueryVariables } from '../../utils/hooks/getBoughtItems';
import ErrorMessage from '../ErrorMessage';
import NoItems from '../NoItems/NoItems';
import { WardrobeParams, useWardrobeParams } from '../../utils/hooks/useWardrobeParams';
import WardrobeItemsPlaceholder from '../WardrobeItemsPlaceholder';
import { TopItems } from './TopItems/TopItems';
import { BoughtItemsRefetchProvider } from './BoughtItemsRefetchContext';
export const formatQueryVariables = ({
  page
}: WardrobeParams) => {
  const variables: GetBoughtItemsQueryVariables = {
    page,
    limit: 12
  };
  return variables;
};

/**
 * Record Initial Page load given query results
 */
const recordInitialPageLoad = (puchaseItemsData: BoughtItemsFragment) => {
  const rumImage = puchaseItemsData.purchasesByPage.edges[0]?.node.variant.image?.mediaPath;
  if (G.isString(rumImage)) {
    recordPageReady({
      resources: [rumImage]
    });
  }
};

/**
 * The `BoughtItemsLayout` is a top level component that handles state
 * related to the initial load, error, empty, and success states
 */
const BoughtItemsLayout = () => {
  const [initialQueryMade, setInitialQueryMade] = useState(false);
  const {
    state,
    send
  } = useBoughtItemsMachine();
  const {
    wardrobeParams,
    reload,
    setWardrobePageNumberParam
  } = useWardrobeParams();
  const {
    loading,
    refetch
  } = useGetBoughtItemsQuery({
    fetchPolicy: 'cache-and-network',
    variables: formatQueryVariables(wardrobeParams),
    onError: e => {
      send('REJECT', {
        error: e.message
      });
      recordMark('get_bought_items_query_completed', {
        graph_query_succeeded: false
      });
    },
    onCompleted: d => {
      const pageCount = d.client?.purchasesCollection.purchasesByPage.pageCount;
      if (G.isNotNullable(pageCount)) {
        // If the user enters a page number in the URL param that is greater than the pageCount
        // returned from graphql, we want to redirect them to the last page with items to show.
        // we don't want to hit RESOLVE in these instances because we want to redirect the user.
        if (pageCount !== 0 && wardrobeParams.page > pageCount) {
          setWardrobePageNumberParam(pageCount);
        } else {
          send('RESOLVE', {
            data: d.client
          });
        }
      }
      if (!initialQueryMade && G.isObject(d.client)) {
        setInitialQueryMade(true);
        recordInitialPageLoad(d.client.purchasesCollection);
      }
    }
  });
  return <BoughtItemsRefetchProvider value={{
    refetch
  }}>
      <ResponsiveSection width="xlarge" mt={2}>
        <WardrobeNavigation />
        {(state.matches('loading') || loading) && <WardrobeItemsPlaceholder />}
        {state.matches('success') && <>
            <TopItems />
            <BoughtItemsSuccess boughtItems={state.context.data} />
          </>}
        {state.matches('empty') && <NoItems />}
        {state.matches('failure') && <ErrorMessage hed="Unable to load this content" subhed="Failed to fetch" onClick={reload} />}
      </ResponsiveSection>
    </BoughtItemsRefetchProvider>;
};
export default BoughtItemsLayout;